import React, { useState, useEffect } from "react";
import { Box, Button, Header, SpaceBetween, StatusIndicator, Badge, ExpandableSection } from "@amzn/awsui-components-react";
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { loadResultsFromS3, getSummaryFindingsTable, formatHeader } from "../Utility";
import { SummaryItemCard } from "../Utility";
import '../styles.scss';
import { CELL_WIDTHS, DEFAULT_CELL_WIDTH, EXCEPTION_STATUSES, SEVERITY_ICONS } from "../Constants";
import { downloadRichPdf } from "../pdfUtils";
import { downloadConsolidatedHistoryExcel } from "../excelUtils";
import DownloadFormatModal from "./DownloadFormatModal";

const FirstpassConsolidatedExceptionsResultComponent = ({ selectedResults, resultsPerRequestId, reportLabel }) => {
  const [consolidatedData, setConsolidatedData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState({ current: 0, total: 0 });
  const [error, setError] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showFormatModal, setShowFormatModal] = useState(false);

  useEffect(() => {
    if (selectedResults && selectedResults.length > 0) {
      generateConsolidatedView();
    }
  }, [selectedResults]);

  const generateConsolidatedView = async () => {
    setIsLoading(true);
    setError(null);
    const total = selectedResults.length;
    setLoadingProgress({ current: 0, total });

    try {
      let totalImportant = 0;
      let totalFyi = 0;
      let totalSections = new Set();
      let completedCount = 0;

      // Load all results concurrently using Promise.all
      const resultPromises = selectedResults.map(async (selectedRow) => {
        const requestId = selectedRow?.id;
        const historyItem = resultsPerRequestId?.[requestId];

        if (!historyItem || !historyItem.response_s3_path) {
          completedCount++;
          setLoadingProgress({ current: completedCount, total });
          return [];
        }

        try {
          const s3Data = await loadResultsFromS3(historyItem);
          const summaryFindings = getSummaryFindingsTable(s3Data);

          completedCount++;
          setLoadingProgress({ current: completedCount, total });

          if (summaryFindings && summaryFindings.length > 0) {
            const exceptionFindings = summaryFindings.filter(
              finding => EXCEPTION_STATUSES.includes(finding.severity)
            );

            return exceptionFindings.map(finding => ({
              section: finding.section || '',
              team: selectedRow.team || '',
              entity_code: selectedRow.entity_code || '',
              entity_name: selectedRow.label || '',
              ...finding,
            }));
          }
          return [];
        } catch (err) {
          console.error(`Error loading results for ${selectedRow.label}:`, err);
          completedCount++;
          setLoadingProgress({ current: completedCount, total });
          return [];
        }
      });

      const results = await Promise.all(resultPromises);
      const allFindings = results.flat();

      // Calculate totals
      for (const finding of allFindings) {
        if (finding.severity === 'important') totalImportant++;
        if (finding.severity === 'fyi') totalFyi++;
        if (finding.section) totalSections.add(finding.section);
      }

      allFindings.sort((a, b) => {
        const sectionA = (a.section || '').toLowerCase();
        const sectionB = (b.section || '').toLowerCase();
        if (sectionA < sectionB) return -1;
        if (sectionA > sectionB) return 1;
        return 0;
      });

      const numberedFindings = allFindings.map((finding, index) => ({
        ...finding,
        no: index + 1,
      }));

      setConsolidatedData({
        findings: numberedFindings,
        stats: {
          totalEntities: selectedResults.length,
          totalImportant,
          totalFyi,
          totalSections: totalSections.size,
        }
      });
    } catch (err) {
      console.error('Error generating consolidated view:', err);
      setError('Failed to generate consolidated view. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadClick = () => {
    if (!consolidatedData) return;
    setShowFormatModal(true);
  };

  const escapeHtml = (str) =>
    String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

  const handleDownloadConfirm = async (format) => {
    setShowFormatModal(false);
    if (!consolidatedData) return;

    setIsDownloading(true);
    const label = reportLabel || 'Consolidated_Report';

    try {
      if (format === "excel") {
        // Build entityDetails from selectedResults to match history page format
        const entityDetails = selectedResults.map(row => ({
          request_id: row.id,
          entity_name: row.label || '',
          entity_code: row.entity_code || '',
          team: row.team || '',
        }));

        await downloadConsolidatedHistoryExcel(
          entityDetails,
          resultsPerRequestId,
          label,
          () => setIsDownloading(false)
        );
      } else {
        // PDF download - same logic as history page
        const { findings, stats } = consolidatedData;
        const columns = ['no', 'section', 'team', 'entity_code', 'entity_name', 'severity', 'description', 'location', 'current_value', 'expected_value', 'impact', 'action_required'];

        let html = `<h1>Consolidated Exception Report</h1>`;
        html += `<h3>${escapeHtml(label)}</h3>`;
        html += `<table><thead><tr>`;
        html += `<th>Entities Included</th><th>Total Important Findings</th><th>Total FYI Findings</th>`;
        html += `</tr></thead><tbody><tr>`;
        html += `<td>${stats.totalEntities}</td><td>${stats.totalImportant}</td><td>${stats.totalFyi}</td>`;
        html += `</tr></tbody></table>`;

        if (findings.length > 0) {
          html += `<h3>Exception Findings</h3>`;
          html += `<table><thead><tr>`;
          for (const col of columns) {
            html += `<th>${escapeHtml(formatHeader(col))}</th>`;
          }
          html += `</tr></thead><tbody>`;
          for (const row of findings) {
            html += `<tr>`;
            for (const col of columns) {
              const value = row[col] !== null && row[col] !== undefined ? row[col] : '';
              html += `<td>${escapeHtml(value)}</td>`;
            }
            html += `</tr>`;
          }
          html += `</tbody></table>`;
        } else {
          html += `<p>No exceptions found across the selected entities.</p>`;
        }

        downloadRichPdf(html, { entity_name: label, current_year: '' }, () => {
          setIsDownloading(false);
        });
      }
    } catch (error) {
      console.error('Error downloading consolidated result:', error);
      setIsDownloading(false);
    }
  };

  if (isLoading) {
    return (
      <Box padding="l">
        <SpaceBetween direction="vertical" size="m">
          <StatusIndicator type="loading">
            Loading consolidated results... ({loadingProgress.current}/{loadingProgress.total} entities processed)
          </StatusIndicator>
        </SpaceBetween>
      </Box>
    );
  }

  if (error) {
    return (
      <Box padding="l">
        <StatusIndicator type="error">{error}</StatusIndicator>
      </Box>
    );
  }

  if (!consolidatedData || consolidatedData.findings.length === 0) {
    return (
      <Box padding="l">
        <SpaceBetween direction="vertical" size="m">
          <Header variant="h2">Consolidated Exception Report</Header>
          <Box color="text-secondary" variant="p">
            No exceptions found across the selected entities.
          </Box>
        </SpaceBetween>
      </Box>
    );
  }

  const columns = [
    'no', 'section', 'team', 'entity_code', 'entity_name', 'severity', 'description', 'location', 
    'current_value', 'expected_value', 'impact', 'action_required'
  ]

  const HEADER_OVERRIDES = {
    entity_code: 'Company Code',
  };

  const columnDefs = columns.map((col) => {
    const colDef = {
      field: col,
      headerName: HEADER_OVERRIDES[col] || formatHeader(col),
      sortable: true,
      filter: true,
      resizable: true,
      wrapText: true,
      autoHeight: true,
      flex: 1,
      minWidth: CELL_WIDTHS[col] ?? DEFAULT_CELL_WIDTH,
    };
    if (col === 'severity') {
      colDef.cellRenderer = (params) => {
        const icon = SEVERITY_ICONS[params.value] || params.value;
        return icon;
      };
    }
    return colDef;
  });

  const defaultColDef = {
    sortable: true,
    filter: true,
    filterParams: {
      buttons: ['reset', 'apply'],
      debounceMs: 200
    },
    resizable: true,
    wrapText: true,
    autoHeight: true,
  };

  const { stats } = consolidatedData;

  return (
    <React.Fragment>
      <div className="firstpass-expandable-section">
        <ExpandableSection
          defaultExpanded
          variant="container"
          headerText={"Consolidated Exception Report"}
        >
          <Box>
            <SpaceBetween direction="vertical" size="l">
              <Box>
                <Box float="left"></Box>
                <Box float="right">
                  <SpaceBetween direction="horizontal" size="xs">
                    <Button
                      iconName="download"
                      variant="icon"
                      onClick={handleDownloadClick}
                      disabled={isDownloading}
                      loading={isDownloading}
                    />
                  </SpaceBetween>
                </Box>
              </Box>
              <div className="summary-item-cards-row">
                <SummaryItemCard header="Entities Included" count={stats.totalEntities} />
                <SummaryItemCard header="Sections With Issues" count={stats.totalSections} />
                <SummaryItemCard header="Total Important Findings" count={stats.totalImportant} />
                <SummaryItemCard header="Total FYI Findings" count={stats.totalFyi} />
              </div>

              <div className="ag-theme-alpine firstpass-findings-grid" style={{ width: '100%' }}>
                <AgGridReact
                  columnDefs={columnDefs}
                  rowData={consolidatedData.findings}
                  defaultColDef={defaultColDef}
                  domLayout="autoHeight"
                  suppressHorizontalScroll={false}
                  enableCellTextSelection={true}
                  pagination={true}
                  paginationPageSize={50}
                  paginationPageSizeSelector={[25, 50, 100, 200]}
                />
              </div>
            </SpaceBetween>
          </Box>
        </ExpandableSection>
      </div>
      <DownloadFormatModal
        visible={showFormatModal}
        onDismiss={() => setShowFormatModal(false)}
        onConfirm={handleDownloadConfirm}
      />
    </React.Fragment>
  );
};

export default FirstpassConsolidatedExceptionsResultComponent;
